import { sectionRenderer } from '@theme/section-renderer';

/**
 * Prices always come from the server (README §15.10).
 *
 * A normal page load is already fresh: Shopify renders it on request and
 * clears its own page cache when a product changes. But a page can also be
 * shown from a browser copy, and then its prices may be old:
 *   1. Back / Forward restored it from the back-forward cache (bfcache);
 *   2. Back / Forward re-used the browser's HTTP copy of the page;
 *   3. it was prefetched before the tap (speculation rules, custom-globals);
 *   4. the tab sat in the background for a while (phones keep tabs for days).
 * In those cases every price on the page is re-read from Shopify, section by
 * section through the Section Rendering API, and only prices that differ are
 * replaced — no flicker when nothing changed, no shimmer over readable text
 * (§11.11). The same rule big stores follow: listings may come from a cache,
 * the money shown is re-checked against the server, and checkout re-prices
 * everything anyway.
 *
 * What is refreshed: Horizon's <product-price> (product page, cards, quick
 * views in sections) and our "Save X%" <price-savings>. The cart drawer is
 * refreshed by assets/custom-tab-bar.js on open and by Horizon on change.
 */

const PRICE_SELECTOR = 'product-price, price-savings';
/** A page kept in the background longer than this is re-checked when it comes back */
const BACKGROUND_MAX_MS = 60 * 1000;

let lastChecked = Date.now();
let hiddenAt = 0;
/** @type {Promise<void> | null} */
let running = null;

/** Was this document delivered from a browser copy rather than rendered for this visit? */
function deliveredFromCopy() {
  const entry = /** @type {PerformanceNavigationTiming & { deliveryType?: string } | undefined} */ (
    performance.getEntriesByType('navigation')[0]
  );
  if (!entry) return false;
  if (entry.deliveryType === 'navigational-prefetch' || entry.deliveryType === 'cache') return true;
  return entry.type === 'back_forward';
}

/**
 * Re-reads every price on the page from the server and fixes the ones that differ.
 * Concurrent calls share one run.
 * @returns {Promise<void>}
 */
export function refreshPrices() {
  running ??= run().finally(() => {
    running = null;
    lastChecked = Date.now();
  });
  return running;
}

async function run() {
  /** @type {Map<string, Element[]>} section id → its price elements */
  const bySection = new Map();
  for (const price of document.querySelectorAll(PRICE_SELECTOR)) {
    const section = price.closest('.shopify-section');
    if (!section?.id || price.closest('dialog')) continue;
    const list = bySection.get(section.id) ?? [];
    list.push(price);
    bySection.set(section.id, list);
  }

  await Promise.all(
    [...bySection].map(async ([sectionId, prices]) => {
      try {
        // useCache = false → a new request; Horizon's in-memory copy is never used for money
        const html = await sectionRenderer.getSectionHTML(sectionId, false);
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        applyFreshPrices(prices, [...fresh.querySelectorAll(PRICE_SELECTOR)]);
      } catch (error) {
        console.warn('[price-fresh]', sectionId, error);
      }
    })
  );
}

/**
 * Pairs each price on the page with the same price in the fresh markup
 * (same element, block and product, in order) and copies the new value over.
 * @param {Element[]} current
 * @param {Element[]} fresh
 */
function applyFreshPrices(current, fresh) {
  /** @type {Map<string, Element[]>} */
  const pool = new Map();
  for (const element of fresh) {
    const key = identity(element);
    pool.set(key, [...(pool.get(key) ?? []), element]);
  }

  for (const element of current) {
    const match = pool.get(identity(element))?.shift();
    if (!match) continue;

    if (element.localName === 'product-price') {
      const target = element.querySelector('[ref="priceContainer"]');
      const source = match.querySelector('[ref="priceContainer"]');
      if (target && source && target.innerHTML !== source.innerHTML) target.innerHTML = source.innerHTML;
    } else if (element.innerHTML !== match.innerHTML) {
      element.innerHTML = match.innerHTML;
    }
  }
}

/** @param {Element} element */
function identity(element) {
  const data = /** @type {HTMLElement} */ (element).dataset;
  return `${element.localName}|${data.blockId ?? ''}|${data.productId ?? ''}`;
}

// 1–3: shown from a browser copy
if (deliveredFromCopy()) {
  requestIdleCallback(() => refreshPrices(), { timeout: 1000 });
}
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshPrices();
});

// 4: back from a long stay in the background
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  if (hiddenAt && Date.now() - hiddenAt > BACKGROUND_MAX_MS && Date.now() - lastChecked > BACKGROUND_MAX_MS) {
    refreshPrices();
  }
});
